import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { InvoiceOverviewTable } from "@/features/invoice-overview/invoiceOverviewTable";
import { useExportInitialQuery } from "@/features/todos/initial-filters";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { InvoicesProvider } from "@/providers/invoices";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function TodosExport({ type }: { type: InvoiceTypeString }) {
  const storageKey = useMemo(() => {
    return `todos-export-${type}`;
  }, [type]);

  const { t } = useTranslation();
  const { page, setPage, limit, setLimit } = useTablePagination();
  const { query } = useExportInitialQuery({ type });
  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    filters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    initialServerSideFilters,
    resetFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
    defaultFilters: query,
  });

  const filtersConfig = useMemo(
    () => ({
      showStatusFilter: false,
      showExportsFilter: false,
      showUploadButton: false,
      showExportAllButton: true,
    }),
    [],
  );

  const columnVisibility = useMemo(
    () => ({
      type: false,
      invoiceNo: true,
      invoiceDate: true,
      startDate: true,
      expenseAccount: true,
      datevBu: true,
      tags: true,
      createdAt: true,
      status: true,
      reviewType: false,
      ocrReviewed: false,
      isBooking: false,
      isDuplicate: false,
    }),
    [],
  );

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    sort,
    order,
  });

  return (
    <div className="flex flex-col h-full gap-4">
      <UsageHelperBanner
        label={t("todos.export_explainer")}
        link="https://tabula-accounting.notion.site/Export-Buchungsdatenservice-einrichten-Daten-exportieren-10deecb29d0480eab835c0cc29f8738d"
        linkText={t("todos.export_help_link")}
      />
      <div className="flex-1 min-h-0">
        <InvoicesProvider invoices={invoices} loading={loading}>
          <InvoiceOverviewTable
            tableName="export"
            showUploadProgress={false}
            filtersConfig={filtersConfig}
            columnVisibility={columnVisibility}
            totalItems={pagination.totalItems}
            currentPage={page}
            totalPages={pagination.totalPages}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            filters={filters}
            setFilters={setFilters}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            sorting={sorting}
            onSortingChange={onSortingChange}
            error={error}
            readOnly
            invoiceType={type}
            initialServerSideFilters={initialServerSideFilters}
            globalServerSideSearch={globalSearch}
            serverSideFilters={serverSideFilters}
            resetFilters={resetFilters}
          />
        </InvoicesProvider>
      </div>
    </div>
  );
}
